import React, { useState, useEffect } from 'react';
import { 
  FolderOpen, Download, Bookmark, LayoutGrid, 
  Sparkles, CheckCircle, AlertTriangle
} from 'lucide-react';
import InputCard from '../common/InputCard';
import TipCard from '../common/TipCard';
import { motion, AnimatePresence } from 'framer-motion';

// StatCard component (inline since it might not exist)
const StatCard = ({ title, value, unit, icon: Icon, color, delay = 0 }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition-all"
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold mt-1 text-gray-800">
            {value}<span className="text-sm font-normal text-gray-400 ml-1">{unit}</span>
          </p>
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
    </motion.div>
  );
};

const DigitalClutterScore = () => {
  const [tabs, setTabs] = useState(15);
  const [downloads, setDownloads] = useState(45);
  const [bookmarks, setBookmarks] = useState(120);
  const [clutterScore, setClutterScore] = useState(0);
  const [cleanliness, setCleanliness] = useState({ 
    level: "Clean Workspace", 
    emoji: "✨", 
    color: "text-green-600", 
    bg: "bg-green-100", 
    message: "Your digital space is well organized!" 
  });
  const [showCleanup, setShowCleanup] = useState(false);

  useEffect(() => {
    let score = 0;
    score += Math.min(35, tabs * 1.2);
    score += Math.min(35, downloads * 0.5);
    score += Math.min(30, bookmarks * 0.2);
    const finalScore = Math.min(100, Math.round(score));
    setClutterScore(finalScore);
    
    if (finalScore < 30) {
      setCleanliness({ 
        level: "Clean Workspace", 
        emoji: "✨", 
        color: "text-green-600", 
        bg: "bg-green-100", 
        message: "Your digital space is well organized!" 
      });
    } else if (finalScore < 60) {
      setCleanliness({ 
        level: "Moderately Messy", 
        emoji: "📁", 
        color: "text-yellow-600", 
        bg: "bg-yellow-100", 
        message: "Some cleanup needed to optimize productivity" 
      });
    } else {
      setCleanliness({ 
        level: "Highly Cluttered", 
        emoji: "🗑️", 
        color: "text-red-600", 
        bg: "bg-red-100", 
        message: "Urgent cleanup recommended!" 
      });
    }
  }, [tabs, downloads, bookmarks]);

  const getCleanupSuggestions = () => {
    const suggestions = [];
    if (tabs > 10) suggestions.push(`Close ${tabs - 5} unused browser tabs`);
    if (downloads > 30) suggestions.push(`Organize or delete ${downloads - 20} old downloads`);
    if (bookmarks > 80) suggestions.push(`Review and remove ${bookmarks - 50} unused bookmarks`);
    return suggestions;
  };

  const getScoreColor = () => {
    if (clutterScore < 30) return "#10b981";
    if (clutterScore < 60) return "#eab308";
    return "#ef4444";
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InputCard title="Digital Clutter Factors" icon={FolderOpen} delay={0.1}>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <LayoutGrid size={16} /> Open Browser Tabs
                </label>
                <span className="text-sm font-semibold text-blue-600">{tabs} tabs</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={tabs} 
                onChange={(e) => setTabs(parseInt(e.target.value))} 
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Minimal</span>
                <span>Normal</span>
                <span>Overwhelming</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Download size={16} /> Downloads (last 30 days)
                </label>
                <span className="text-sm font-semibold text-blue-600">{downloads} files</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="200" 
                value={downloads} 
                onChange={(e) => setDownloads(parseInt(e.target.value))} 
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Bookmark size={16} /> Unsorted Bookmarks
                </label>
                <span className="text-sm font-semibold text-blue-600">{bookmarks} bookmarks</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="500" 
                value={bookmarks} 
                onChange={(e) => setBookmarks(parseInt(e.target.value))} 
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </InputCard>

        <div className="space-y-5">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-white rounded-xl shadow-sm p-6 border text-center"
          >
            <div className="relative w-48 h-48 mx-auto mb-4">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="96" cy="96" r="84" stroke="#e5e7eb" strokeWidth="16" fill="none" />
                <circle 
                  cx="96" cy="96" r="84" 
                  stroke={getScoreColor()} 
                  strokeWidth="16" 
                  fill="none" 
                  strokeDasharray={`${(clutterScore / 100) * 527.78} 527.78`} 
                  strokeLinecap="round"
                  className="transition-all duration-700"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl mb-1">{cleanliness.emoji}</span>
                <span className="text-3xl font-bold text-gray-800">{clutterScore}</span>
                <span className="text-xs text-gray-400">/100</span>
              </div>
            </div>
            <div className={`inline-block px-4 py-1.5 rounded-full ${cleanliness.bg} ${cleanliness.color} font-semibold text-base mb-2`}>
              {cleanliness.level}
            </div>
            <p className="text-sm text-gray-500 mt-2">{cleanliness.message}</p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCleanup(!showCleanup)}
              className="mt-4 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2 mx-auto"
            >
              <Sparkles size={18} />
              Get Cleanup Suggestions
            </motion.button>
          </motion.div>
          
          <StatCard 
            title="Productivity Impact" 
            value={clutterScore > 50 ? "High" : clutterScore > 25 ? "Medium" : "Low"} 
            unit="" 
            icon={AlertTriangle} 
            color={clutterScore > 50 ? "bg-red-500" : clutterScore > 25 ? "bg-yellow-500" : "bg-green-500"} 
            delay={0.3} 
          />
        </div>
      </div>

      <AnimatePresence>
        {showCleanup && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-xl shadow-sm p-5 border border-blue-100"
          >
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Sparkles size={18} className="text-blue-500" />
              Suggested Cleanup Actions
            </h3>
            <div className="space-y-2">
              {getCleanupSuggestions().map((suggestion, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <CheckCircle size={16} className="text-green-500" />
                  <span className="text-gray-700">{suggestion}</span>
                </div>
              ))}
              {getCleanupSuggestions().length === 0 && (
                <div className="flex items-center gap-3 p-2 text-green-600">
                  <CheckCircle size={16} />
                  <span>Great job! Your digital space is already well organized!</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TipCard icon={LayoutGrid} title="Tab Management" text="Use tab groups or bookmarks instead of keeping 20+ tabs open" color="blue" />
        <TipCard icon={Download} title="Downloads Folder" text="Create subfolders and delete files you no longer need" color="green" />
        <TipCard icon={Bookmark} title="Bookmark Audit" text="Review bookmarks monthly and remove dead links" color="purple" />
      </div>
    </motion.div>
  );
};

export default DigitalClutterScore;